import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step5Review.module.css';

/**
 * Map Sharetribe API error codes to friendly messages and which step to fix.
 */
const API_ERROR_MAP = {
  'email-taken': {
    message: 'This email address is already registered. Please use a different email.',
    hint: 'Go back to Step 2 and change the email address.',
    fixStep: 2,
    fixLabel: 'Edit Contact Info',
  },
  'email-invalid': {
    message: 'The email address you entered is not valid.',
    hint: 'Go back to Step 2 and enter a valid email.',
    fixStep: 2,
    fixLabel: 'Edit Contact Info',
  },
  'password-too-short': {
    message: 'Password is too short — minimum 8 characters.',
    hint: 'Go back to Step 2 and set a longer password.',
    fixStep: 2,
    fixLabel: 'Edit Contact Info',
  },
};

/** Extract the most useful error message from a storableError object */
const getSignupErrorInfo = error => {
  if (!error) return null;
  const code = error?.apiErrors?.[0]?.code;
  const apiTitle = error?.apiErrors?.[0]?.title;
  if (code && API_ERROR_MAP[code]) return { ...API_ERROR_MAP[code], code };
  // Fall back to the API-provided title, then generic message
  return {
    message: apiTitle || error?.message || 'Registration failed. Please review your information.',
    hint: null,
    fixStep: null,
    fixLabel: null,
    code,
  };
};

/**
 * Single data row inside a summary section.
 */
const Row = ({ label, value }) =>
  value ? (
    <div className={css.row}>
      <span className={css.label}>{label}</span>
      <span className={css.value}>{value}</span>
    </div>
  ) : null;

/**
 * A collapsible section of the review summary.
 */
const Section = ({ title, stepNum, onEdit, children }) => (
  <div className={css.section}>
    <div className={css.sectionHeader}>
      <span className={css.sectionTitle}>{title}</span>
      <button type="button" className={css.editBtn} onClick={() => onEdit(stepNum)}>
        ✏ Edit
      </button>
    </div>
    <div className={css.sectionBody}>{children}</div>
  </div>
);

const Step5Review = ({
  prevStep,
  goToStep,
  formData,
  onSubmit,
  submitInProgress,
  submitError,
  className,
  rootClassName,
}) => {
  const classes = classNames(rootClassName || css.root, className);

  const acceptedCount = ['agreement1', 'agreement2', 'agreement3', 'agreement4'].filter(
    k => formData[k]
  ).length;

  const agreementLabels = {
    agreement1: 'ChinaDolphins Partnership Agreement',
    agreement2: 'Privacy Policy',
    agreement3: 'Product Authenticity Declaration',
    agreement4: 'Quality Commitment',
  };

  const [confirmed, setConfirmed] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState(false);

  const handleSubmit = e => {
    e.preventDefault();
    if (!confirmed) {
      setConfirmError(true);
      return;
    }
    setConfirmError(false);
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={classNames(classes, css.form)}>

      <div className={css.header}>
        <span className={css.step}>Step 5 of 5</span>
        <h2 className={css.title}>Review & Submit</h2>
        <p className={css.description}>
          Review all your information below. Click <strong>Edit</strong> on any section to correct it.
        </p>
      </div>

      <hr className={css.divider} />

      {/* ── API error ── */}
      {submitError && (() => {
        const errInfo = getSignupErrorInfo(submitError);
        return (
          <div className={css.apiError}>
            <div className={css.apiErrorIcon}>⚠</div>
            <div className={css.apiErrorBody}>
              <strong>Registration failed.</strong>
              <p className={css.apiErrorMsg}>{errInfo.message}</p>
              {errInfo.hint && <p className={css.apiErrorHint}>{errInfo.hint}</p>}
              <div className={css.apiErrorActions}>
                {errInfo.fixStep ? (
                  <button
                    type="button"
                    className={css.goBackBtn}
                    onClick={() => goToStep(errInfo.fixStep)}
                  >
                    {errInfo.fixLabel}
                  </button>
                ) : (
                  <>
                    <button type="button" className={css.goBackBtn} onClick={() => goToStep(1)}>
                      Edit Company Info
                    </button>
                    <button type="button" className={css.goBackBtn} onClick={() => goToStep(2)}>
                      Edit Contact Info
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Section 1: Company Information ── */}
      <Section title="Company Information" stepNum={1} onEdit={goToStep}>
        <Row label="Company Name" value={formData.companyName} />
        <Row label="Business License No." value={formData.businessLicense} />
        <Row label="Registration No." value={formData.registrationNumber} />
        <Row
          label="Province / City"
          value={
            formData.province || formData.city
              ? [formData.province, formData.city].filter(Boolean).join(', ')
              : null
          }
        />
        <Row label="Factory Address" value={formData.factoryAddress} />
        {!formData.companyName && (
          <div className={css.missingWarning}>⚠ Company information is incomplete — please edit.</div>
        )}
      </Section>

      {/* ── Section 2: Contact Person ── */}
      <Section title="Contact Person" stepNum={2} onEdit={goToStep}>
        <Row label="Full Name" value={formData.contactPersonName} />
        <Row label="Designation" value={formData.designation} />
        <Row label="Email" value={formData.email} />
        <Row label="Phone" value={formData.phone} />
        <Row label="WhatsApp" value={formData.whatsapp} />
        <Row label="WeChat ID" value={formData.wechat} />
        <Row label="Alt. Email" value={formData.alternativeEmail} />
        {!formData.email && (
          <div className={css.missingWarning}>⚠ Contact information is incomplete — please edit.</div>
        )}
      </Section>

      {/* ── Section 3: Industry Details ── */}
      <Section title="Industry Details" stepNum={3} onEdit={goToStep}>
        <Row label="Primary Industry" value={formData.primaryIndustry} />
        <Row label="Secondary Industry" value={formData.secondaryIndustry} />
        <Row label="Products / Services" value={formData.products} />
        <Row label="Production Capacity" value={formData.capacity} />
        {!formData.primaryIndustry && (
          <div className={css.missingWarning}>⚠ Primary industry not selected — please edit.</div>
        )}
      </Section>

      {/* ── Section 4: Agreements ── */}
      <Section title="Agreements" stepNum={4} onEdit={goToStep}>
        {Object.entries(agreementLabels).map(([key, label]) => (
          <div key={key} className={css.row}>
            <span className={css.label}>{label}</span>
            <span className={formData[key] ? css.green : css.red}>
              {formData[key] ? '✓ Accepted' : '✗ Not accepted'}
            </span>
          </div>
        ))}
        {acceptedCount < 4 && (
          <div className={css.missingWarning}>
            ⚠ {4 - acceptedCount} agreement(s) not accepted — please edit.
          </div>
        )}
      </Section>

      {/* ── Confirmation checkbox ── */}
      <div className={classNames(css.confirm, { [css.confirmError]: confirmError })}>
        <input
          type="checkbox"
          id="confirmCheck"
          checked={confirmed}
          onChange={e => {
            setConfirmed(e.target.checked);
            if (e.target.checked) setConfirmError(false);
          }}
        />
        <label htmlFor="confirmCheck">
          I confirm that all the information provided is true and accurate.
          <span className={css.required}> *</span>
        </label>
      </div>
      {confirmError && (
        <div className={css.confirmErrorMsg}>Please confirm that your information is accurate.</div>
      )}

      <div className={css.buttonContainer}>
        <button type="button" onClick={prevStep} className={css.previous}>
          ← Previous
        </button>
        <button
          type="submit"
          className={classNames(css.submit, { [css.submitDisabled]: submitInProgress })}
          disabled={submitInProgress}
        >
          {submitInProgress ? 'Submitting…' : 'Submit Application →'}
        </button>
      </div>

    </form>
  );
};

Step5Review.defaultProps = {
  className: null,
  rootClassName: null,
  formData: {},
  submitInProgress: false,
  submitError: null,
};

Step5Review.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  formData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  submitInProgress: PropTypes.bool,
  submitError: PropTypes.object,
};

export default Step5Review;
